import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle, Mail } from 'lucide-react';
import { toast } from 'sonner';

interface PendingUser {
  user_id: string;
  user_email: string;
  company_id: string;
  company_name: string;
  role: string;
  days_pending: number;
}

export function NDAPendingUsers() {
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState<string | null>(null);

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const fetchPendingUsers = async () => {
    setLoading(true);

    // Get active NDA
    const { data: activeNDA } = await supabase
      .from('legal_documents')
      .select('id')
      .eq('document_type', 'NDA')
      .is('active_until', null)
      .single();

    if (!activeNDA) {
      setLoading(false);
      return;
    }

    // Get all investors and observers
    const { data: userRoles } = await supabase
      .from('user_roles')
      .select('user_id, company_id, role')
      .in('role', ['investor', 'observer']);

    if (!userRoles) {
      setLoading(false);
      return;
    }

    // Check which ones haven't accepted
    const pending: PendingUser[] = [];

    for (const role of userRoles) {
      const { data: acceptance } = await supabase
        .from('nda_acceptances')
        .select('id')
        .eq('user_id', role.user_id)
        .eq('company_id', role.company_id)
        .eq('nda_version', activeNDA.id)
        .maybeSingle();

      if (!acceptance) {
        // Fetch user and company details
        const [userRes, companyRes, roleCreatedRes] = await Promise.all([
          supabase.auth.admin.getUserById(role.user_id),
          supabase.from('companies').select('name').eq('id', role.company_id).single(),
          supabase
            .from('user_roles')
            .select('created_at')
            .eq('user_id', role.user_id)
            .eq('company_id', role.company_id)
            .single(),
        ]);

        const daysPending = roleCreatedRes.data
          ? Math.floor(
              (Date.now() - new Date(roleCreatedRes.data.created_at).getTime()) /
                (1000 * 60 * 60 * 24)
            )
          : 0;

        pending.push({
          user_id: role.user_id,
          user_email: userRes.data.user?.email || 'Unknown',
          company_id: role.company_id,
          company_name: companyRes.data?.name || 'Unknown',
          role: role.role,
          days_pending: daysPending,
        });
      }
    }

    setPendingUsers(pending);
    setLoading(false);
  };

  const handleSendReminder = async (userId: string, companyId: string) => {
    setReminding(userId);
    try {
      const { error } = await supabase.functions.invoke('nda-send-reminder', {
        body: { userId, companyId },
      });

      if (error) throw error;
      toast.success('Reminder sent successfully');
    } catch (error) {
      console.error('Reminder error:', error);
      toast.error('Failed to send reminder');
    } finally {
      setReminding(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (pendingUsers.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <AlertTriangle className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No pending NDA acceptances</p>
        <p className="text-sm">
          All users with investor/observer roles have accepted the current NDA
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Company</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Days Pending</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pendingUsers.map((user) => (
            <TableRow key={`${user.user_id}-${user.company_id}`}>
              <TableCell className="font-medium">{user.user_email}</TableCell>
              <TableCell>{user.company_name}</TableCell>
              <TableCell>
                <Badge variant="secondary" className="capitalize">
                  {user.role}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {user.days_pending > 7 && <AlertTriangle className="h-4 w-4 text-warning" />}
                  <span className={user.days_pending > 7 ? 'text-warning font-medium' : ''}>
                    {user.days_pending} days
                  </span>
                </div>
              </TableCell>
              <TableCell className="text-right">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSendReminder(user.user_id, user.company_id)}
                  disabled={reminding === user.user_id}
                >
                  {reminding === user.user_id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-1" />
                      Send Reminder
                    </>
                  )}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
